import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';

import { useDeleteTagAssignment, usePlaces, useTagAssignmentsForForm } from '../../hooks';

import TagAssignmentsDestinationSearch from './TagAssignmentsDestinationSearch';

interface Props {
  tagId?: string;
  selectedPlaceIds: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
}

const AccordionStyled = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&:not(:last-child)': {
    marginBottom: theme.spacing(1),
  },
}));

const AccordionSummaryStyled = styled(AccordionSummary)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

const AccordionDetailsStyled = styled(AccordionDetails)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
}));

export default function TagAssignmentsDestination({ tagId, selectedPlaceIds, onSelectionChange }: Props) {
  const [savedExpanded, setSavedExpanded] = useState(true);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const tagAssignmentsData = useTagAssignmentsForForm({
    tagId,
  });
  const savedAssignments = tagAssignmentsData.assignments;
  const isLoadingAssignments = tagAssignmentsData.isLoading;

  const deleteTagAssignment = useDeleteTagAssignment();

  const destinationAssignments = tagAssignmentsData.savedAssignments.filter((a) => a.entityType === 'DESTINATION');
  const savedPlaceIds = destinationAssignments.map((a) => a.entityId);

  const placesQueries = usePlaces({
    placeIds: savedPlaceIds,
    enabled: savedPlaceIds.length > 0 && !isLoadingAssignments,
  });

  const placeNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    placesQueries.forEach((query, index) => {
      const placeId = savedPlaceIds[index];
      if (query.data?.result?.result) {
        const place = query.data.result.result;
        const name = place.name || place.nameFull || placeId;
        map.set(placeId, name);
      } else if (placeId) {
        map.set(placeId, placeId);
      }
    });
    return map;
  }, [placesQueries, savedPlaceIds]);

  const handleDeleteClick = (assignmentId: string) => {
    if (savedAssignments.length === 1) {
      enqueueSnackbar('Cannot delete the unique assignment. A tag must have at least one assignment.', {
        variant: 'error',
      });
      return;
    }
    setAssignmentToDelete(assignmentId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!assignmentToDelete) {
      return;
    }
    await deleteTagAssignment.mutateAsync(assignmentToDelete);
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDestinationsChange = (newPlaceIds: Array<string>) => {
    if (tagId && savedPlaceIds.length > 0) {
      const combinedPlaceIds = [...savedPlaceIds, ...newPlaceIds.filter((id) => !savedPlaceIds.includes(id))];
      onSelectionChange(combinedPlaceIds);
    } else {
      onSelectionChange(newPlaceIds);
    }
  };

  const handleDestinationRemove = (placeIdToRemove: string) => {
    if (tagId && savedPlaceIds.includes(placeIdToRemove)) {
      return;
    }
    onSelectionChange(selectedPlaceIds.filter((id) => id !== placeIdToRemove));
  };

  return (
    <Box>
      {tagId && (
        <AccordionStyled expanded={savedExpanded} onChange={(_, expanded) => setSavedExpanded(expanded)}>
          <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">
              Saved Assignments ({destinationAssignments.length})
            </Typography>
          </AccordionSummaryStyled>
          <AccordionDetailsStyled>
            <Box>
              {isLoadingAssignments ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <CircularProgress />
                </Box>
              ) : destinationAssignments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No saved destination assignments
                </Typography>
              ) : (
                <TableContainerStyled>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Entity ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {destinationAssignments.map((assignment) => {
                        const isDeleting = deleteTagAssignment.isPending && assignmentToDelete === assignment.id;
                        const placeName = placeNamesMap.get(assignment.entityId) || assignment.entityId;
                        const isLoadingPlace = placesQueries.some(
                          (query, idx) => savedPlaceIds[idx] === assignment.entityId && query.isLoading,
                        );
                        return (
                          <TableRow key={assignment.id}>
                            <TableCell>{assignment.id}</TableCell>
                            <TableCell>{assignment.entityId}</TableCell>
                            <TableCell>
                              {isLoadingPlace ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Typography variant="body2">{placeName}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(assignment.id)}
                                disabled={isDeleting}
                              >
                                {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainerStyled>
              )}
            </Box>
          </AccordionDetailsStyled>
        </AccordionStyled>
      )}
      <AccordionStyled expanded={searchExpanded} onChange={(_, expanded) => setSearchExpanded(expanded)}>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Add New Assignments
          </Typography>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          <Box>
            <Box mb={3}>
              <Typography variant="subtitle2" mb={1}>
                Select Destinations
              </Typography>
              <TagAssignmentsDestinationSearch
                onSelectDestinations={handleDestinationsChange}
                onRemoveDestination={handleDestinationRemove}
                savedPlaceIds={tagId ? savedPlaceIds : []}
                selectedPlaceIds={selectedPlaceIds}
              />
            </Box>
          </Box>
        </AccordionDetailsStyled>
      </AccordionStyled>
      <Dialog open={deleteDialogOpen} onClose={deleteTagAssignment.isPending ? undefined : handleDeleteCancel}>
        <DialogTitle>Delete Assignment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this assignment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="text" disabled={deleteTagAssignment.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            autoFocus
            disabled={deleteTagAssignment.isPending}
            startIcon={deleteTagAssignment.isPending ? <CircularProgress size={16} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
